import React, { useMemo, useState, useCallback } from "react"
import { Link } from "react-router-dom"
import * as Yup from "yup"
import { useFormik } from "formik"
import { toast } from "react-toastify"
import {
  Col,
  Row,
  UncontrolledTooltip,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  Input,
  FormFeedback,
  Label,
  Card,
  CardBody,
  Button,
} from "reactstrap"

// Custom Components
import TableContainer from "../../../components/Common/TableContainer"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import DeleteModal from "../../../components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"

// Custom Hooks
import {
  useCreateEngineCode,
  useDeleteEngineCode,
  useEditEngineCode,
  useEngineCodes,
} from "hooks/engineCodeHook"
import { useListParams } from "hooks/useListParams"

// Utils
import { formatReadableDate } from "utils/formatDate"
import { useModels } from "hooks/modelHook"
import { modelToModelIdsNumber } from "utils/modelUtils"
import BadgeList from "components/Common/BadgeList"
import ButtonCheckboxList from "components/Common/ButtonCheckboxList"

const EngineCodeList = () => {
  document.title = "Engine Code List"

  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [engineCode, setEngineCode] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)

  const { params, setPage, setSearch, resetParams, hasActiveFilters } =
    useListParams({
      page: 1,
      search: "",
    })

  const queryParams = useMemo(
    () => ({
      page: params.page,
      per_page: 10,
      with_models: true,
      ...(params.search && { search: params.search }),
    }),
    [params.page, params.search]
  )

  const {
    data: engineCodesResponse,
    isLoading,
    refetch,
    isRefetching,
  } = useEngineCodes(queryParams)

  const { data: modelsResponse, isLoading: modelsFetchLoading } = useModels(
    { all: true },
    modal
  )

  const { mutateAsync: createEngineCode, isPending: createLoading } =
    useCreateEngineCode()
  const { mutateAsync: editEngineCode, isPending: editLoading } =
    useEditEngineCode()
  const { mutateAsync: deleteEngineCode, isPending: deleteLoading } =
    useDeleteEngineCode()

  const toggle = useCallback(() => {
    if (modal) {
      setModal(false)
      setEngineCode(null)
      setIsEdit(false)
    } else {
      setModal(true)
    }
  }, [modal])

  const handleAddNewClick = useCallback(() => {
    setEngineCode(null)
    setIsEdit(false)
    toggle()
  }, [toggle])

  const handleEditClick = useCallback(
    arg => {
      setEngineCode({ id: arg.id, name: arg.name })
      setIsEdit(true)
      toggle()
    },
    [toggle]
  )

  const onClickDelete = useCallback(code => {
    setEngineCode(code)
    setDeleteModal(true)
  }, [])

  const handleDelete = useCallback(async () => {
    if (engineCode?.id) {
      try {
        const res = await deleteEngineCode(engineCode.id)
        toast.success(res?.message ?? "Engine code deleted successfully.")
        setDeleteModal(false)
      } catch (error) {
        toast.error("Failed to delete engine code.")
      }
    }
  }, [engineCode, deleteEngineCode])

  const validationSchema = Yup.object({
    name: Yup.string()
      .trim()
      .min(2, "Name must be at least 2 characters long")
      .required("Please enter engine code name"),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      const payload = {
        name: values.name,
        modelIds: values.selectedModels?.map(n => Number(n)),
      }
      const action = isEdit
        ? editEngineCode({ id: engineCode.id, ...payload })
        : createEngineCode(payload)

      try {
        const res = await action
        toast.success(
          res?.message ??
            `Engine code ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        toggle()
      } catch (error) {
        toast.error("Failed to save engine code.")
      }
    },
    [isEdit, engineCode, editEngineCode, createEngineCode, toggle]
  )

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: engineCode?.name || "",
      selectedModels: modelToModelIdsNumber(engineCode?.models) || [],
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
        cell: ({ row }) => (
          <Link to="#" className="text-body fw-bold">
            {row.original.id}
          </Link>
        ),
      },
      {
        header: "Name",
        accessorKey: "name",
      },
      {
        header: "Slug",
        accessorKey: "slug",
      },
      {
        header: "Models",
        accessorKey: "models",
        cell: ({ row }) => <BadgeList items={row.original.models} />,
      },
      {
        header: "Created",
        accessorKey: "created_at",
        cell: ({ row }) =>
          row.original.created_at
            ? formatReadableDate(row.original.created_at)
            : "-",
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <ul className="list-unstyled hstack gap-1 mb-0">
            <li>
              <button
                type="button"
                className="btn btn-sm btn-soft-info"
                onClick={() => handleEditClick(row.original)}
                id={`edittooltip-${row.original.id}`}
              >
                <i className="mdi mdi-pencil-outline" />
                <UncontrolledTooltip
                  placement="top"
                  target={`edittooltip-${row.original.id}`}
                >
                  Edit
                </UncontrolledTooltip>
              </button>
            </li>
            <li>
              <button
                type="button"
                className="btn btn-sm btn-soft-danger"
                onClick={() => onClickDelete(row.original)}
                id={`deletetooltip-${row.original.id}`}
              >
                <i className="mdi mdi-delete-outline" />
                <UncontrolledTooltip
                  placement="top"
                  target={`deletetooltip-${row.original.id}`}
                >
                  Delete
                </UncontrolledTooltip>
              </button>
            </li>
          </ul>
        ),
      },
    ],
    [handleEditClick, onClickDelete]
  )

  const engineCodesData = engineCodesResponse?.data?.data || []
  const paginationInfo = engineCodesResponse?.data

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        loading={deleteLoading}
        onCloseClick={() => setDeleteModal(false)}
      />
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Engine Codes" breadcrumbItem="Engine Code List" />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody className="border-bottom">
                  <div className="d-flex align-items-center">
                    <h5 className="mb-0 card-title flex-grow-1">
                      Engine Code List
                    </h5>
                    <div className="flex-shrink-0">
                      <Button
                        color="primary"
                        onClick={handleAddNewClick}
                        className="me-1"
                      >
                        Add New Engine Code
                      </Button>
                      <Button
                        color="light"
                        onClick={refetch}
                        className="me-1"
                        id="refreshTooltip"
                      >
                        <i
                          className={`mdi mdi-refresh ${
                            isRefetching ? "mdi-spin" : ""
                          }`}
                        />
                        <UncontrolledTooltip
                          placement="top"
                          target="refreshTooltip"
                        >
                          Refresh
                        </UncontrolledTooltip>
                      </Button>
                      {hasActiveFilters && (
                        <Button
                          color="danger"
                          onClick={resetParams}
                          className="me-1"
                          id="resetTooltip"
                        >
                          <i className="mdi mdi-close" />
                          <UncontrolledTooltip
                            placement="top"
                            target="resetTooltip"
                          >
                            Reset Filters
                          </UncontrolledTooltip>
                        </Button>
                      )}
                    </div>
                  </div>
                </CardBody>
                <CardBody>
                  <Row className="mb-3">
                    <Col sm={4}>
                      <GlobalInputSearch
                        value={params.search}
                        onChange={setSearch}
                      />
                    </Col>
                  </Row>
                  <TableContainer
                    columns={columns}
                    data={engineCodesData}
                    isPagination={true}
                    paginationInfo={paginationInfo}
                    onPageChange={setPage}
                    loading={isLoading || isRefetching}
                    tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
                    pagination="pagination"
                    paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Modal isOpen={modal} toggle={toggle} centered>
            <ModalHeader toggle={toggle} tag="h4">
              {isEdit ? "Edit Engine Code" : "Add Engine Code"}
            </ModalHeader>
            <ModalBody>
              <Form
                onSubmit={e => {
                  e.preventDefault()
                  validation.handleSubmit()
                }}
              >
                <div className="mb-3">
                  <Label htmlFor="name">Engine Code Name</Label>
                  <Input
                    name="name"
                    id="name"
                    type="text"
                    placeholder="Enter engine code name"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.name || ""}
                    invalid={
                      validation.touched.name && !!validation.errors.name
                    }
                  />
                  {validation.touched.name && validation.errors.name ? (
                    <FormFeedback type="invalid">
                      {validation.errors.name}
                    </FormFeedback>
                  ) : null}
                </div>

                <div className="mb-3">
                  <Label>Select Models for This Engine Code</Label>
                  <div className="mb-3">
                    <ButtonCheckboxList
                      items={modelsResponse?.data}
                      name="selectedModels"
                      selected={validation.values.selectedModels}
                      onChange={newSelected =>
                        validation.setFieldValue("selectedModels", newSelected)
                      }
                      loading={modelsFetchLoading}
                    />
                  </div>
                </div>

                <div className="text-end">
                  <Button
                    color="success"
                    type="submit"
                    disabled={editLoading || createLoading}
                  >
                    {isEdit ? "Save Changes" : "Add Engine Code"}
                    {(editLoading || createLoading) && "..."}
                  </Button>
                </div>
              </Form>
            </ModalBody>
          </Modal>
        </div>
      </div>
    </React.Fragment>
  )
}

export default EngineCodeList
